import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { toast } from "sonner";
import { Boxes, AlertTriangle, Loader2 } from "lucide-react";

const CODES = ["T1", "T2", "T3"];
const COLS = [
  { k: "total", label: "Total", cls: "text-[#0F1A3D]" },
  { k: "disponible", label: "Disponible", cls: "text-[#1F5B2F]" },
  { k: "reserve", label: "Réservé", cls: "text-[#8B4E12]" },
  { k: "vendu", label: "Vendu", cls: "text-[#2B2B2B]" },
];

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || "Erreur";
};

function StockTable({ stock }) {
  const byCode = Object.fromEntries((stock?.typologies || []).map((t) => [t.code, t]));
  const hasLoue = (stock?.typologies || []).some((t) => t.loue > 0);
  const cols = hasLoue ? [...COLS, { k: "loue", label: "Loué", cls: "text-[#1E4273]" }] : COLS;
  return (
    <table className="w-full text-sm" data-testid="stock-table">
      <thead className="bg-[#F1F3FB] text-[#4B5775] text-xs uppercase tracking-[0.05em]">
        <tr>
          <th className="text-left px-4 py-2.5 font-semibold">Typologie</th>
          {cols.map((c) => <th key={c.k} className="text-right px-4 py-2.5 font-semibold">{c.label}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-[#E2E5F1]">
        {CODES.map((code) => {
          const t = byCode[code] || {};
          return (
            <tr key={code} data-testid={`stock-row-${code}`}>
              <td className="px-4 py-2.5 font-bold text-[#0F1A3D]">
                <span className="font-mono bg-[#1E3A8A] text-white text-[10px] px-1.5 py-0.5 rounded mr-2">{code}</span>
                {t.nom || `Villa Type ${code[1]}`}
              </td>
              {cols.map((c) => (
                <td key={c.k} className={`px-4 py-2.5 text-right font-mono font-bold ${c.cls}`} data-testid={`stock-${code}-${c.k}`}>
                  {t[c.k] || 0}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function InitForm({ immeubleId, stock, onDone }) {
  const [qty, setQty] = useState({ T1: "", T2: "", T3: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const byCode = Object.fromEntries((stock?.typologies || []).map((t) => [t.code, t.total]));
    setQty({ T1: byCode.T1 ?? 0, T2: byCode.T2 ?? 0, T3: byCode.T3 ?? 0 });
  }, [stock]);

  const submit = async () => {
    const typologies = CODES.map((code) => ({ code, quantity: Number(qty[code] || 0) }));
    if (typologies.some((t) => !Number.isInteger(t.quantity) || t.quantity < 0)) {
      toast.error("Quantités invalides"); return;
    }
    if (!window.confirm(`Initialiser le stock physique :\n${typologies.map((t) => `${t.code} = ${t.quantity}`).join("\n")}\n\nAucune réduction n'est possible ensuite.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/immeubles/${immeubleId}/initialize-stock`, { typologies });
      const n = Object.values(data.created || {}).reduce((s, c) => s + c.created, 0);
      toast.success(`Stock initialisé : ${n} unité(s) créée(s)`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 border-t border-[#E2E5F1] bg-[#F8FAFC]" data-testid="stock-init-form">
      <div className="label-tiny mb-2">Initialisation (quantités globales)</div>
      <div className="flex flex-wrap items-end gap-3">
        {CODES.map((code) => (
          <label key={code} className="flex flex-col text-[11px] font-bold text-[#4B5775]">
            {code}
            <input type="number" min={0} value={qty[code]}
                   onChange={(e) => setQty((q) => ({ ...q, [code]: e.target.value }))}
                   className="mt-1 h-9 w-28 px-2 rounded-md border border-[#E2E5F1] bg-white font-mono text-sm text-[#0F1A3D] focus:outline-none focus:border-[#1E3A8A]"
                   data-testid={`stock-init-${code}`} />
          </label>
        ))}
        <button type="button" onClick={submit} disabled={busy}
                className="h-9 px-4 rounded-md bg-[#0F1A3D] hover:bg-[#1E3A8A] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60 transition-colors"
                data-testid="stock-init-submit">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Initialiser le stock
        </button>
      </div>
      <p className="text-[11px] text-[#4B5775] mt-2">
        Aucun numéro de lot n'est généré : le numéro réel (ex : 433, 821) est saisi lors de la souscription / du contrat
        et ne sert jamais au calcul du stock.
      </p>
    </div>
  );
}

function ACorriger({ items, isAdmin, onDone }) {
  const [codes, setCodes] = useState({});
  if (!items?.length) return null;
  const fix = async (u) => {
    const code = codes[u.id];
    if (!code) { toast.error("Choisissez T1/T2/T3"); return; }
    const motif = window.prompt("Motif de la correction (min. 5 caractères) :");
    if (!motif) return;
    try {
      await api.post(`/unites/${u.id}/correct-typologie`, { typologie_code: code, motif });
      toast.success(`${u.nom} → ${code}`);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="p-4 border-t border-[#F4CFCE] bg-[#FDF5F5]" data-testid="stock-a-corriger">
      <div className="flex items-center gap-2 text-[#C83B3B] font-bold text-sm mb-2">
        <AlertTriangle className="w-4 h-4" /> Unités à corriger · {items.length} (typologie absente — exclues du stock)
      </div>
      <div className="space-y-1.5 max-h-60 overflow-y-auto">
        {items.map((u) => (
          <div key={u.id} className="flex items-center gap-2 text-xs" data-testid={`a-corriger-${u.id}`}>
            <span className="font-semibold text-[#0F1A3D] flex-1">{u.nom} {u.numero_lot ? `· N°${u.numero_lot}` : ""} · {u.statut}</span>
            {isAdmin && (
              <>
                <select value={codes[u.id] || ""} onChange={(e) => setCodes((c) => ({ ...c, [u.id]: e.target.value }))}
                        className="h-7 px-2 rounded border border-[#E2E5F1] bg-white" data-testid={`a-corriger-select-${u.id}`}>
                  <option value="">—</option>
                  {CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button type="button" onClick={() => fix(u)} className="h-7 px-2.5 rounded bg-[#C83B3B] text-white font-bold"
                        data-testid={`a-corriger-btn-${u.id}`}>Corriger</button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StockPhysiquePanel({ immeubleId, isAdmin, refreshKey, onChanged }) {
  const [stock, setStock] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/immeubles/${immeubleId}/stock`);
      setStock(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [immeubleId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const done = async () => { await load(); onChanged?.(); };

  return (
    <div className="tpco-card overflow-hidden mb-6" data-testid="stock-physique-panel">
      <div className="px-6 py-4 border-b border-[#E2E5F1] flex items-center justify-between gap-4">
        <div>
          <div className="label-tiny mb-0.5">Stock réel (collection unités)</div>
          <h3 className="font-display text-lg font-bold inline-flex items-center gap-2">
            <Boxes className="w-5 h-5 text-[#1E3A8A]" /> Gestion du stock physique
          </h3>
        </div>
        {stock && (
          <div className="text-right text-xs text-[#4B5775]" data-testid="stock-totals">
            <span className="font-mono font-bold text-[#0F1A3D] text-base">{stock.total_unites}</span> unités ·{" "}
            <span className="font-mono font-bold text-[#1F5B2F]">{stock.total_dispo}</span> dispo
          </div>
        )}
      </div>
      {!stock ? (
        <div className="p-6 text-sm text-[#9CA3B5]">Chargement…</div>
      ) : (
        <>
          <div className="overflow-x-auto"><StockTable stock={stock} /></div>
          <ACorriger items={stock.a_corriger} isAdmin={isAdmin} onDone={done} />
          {isAdmin && <InitForm immeubleId={immeubleId} stock={stock} onDone={done} />}
        </>
      )}
    </div>
  );
}
